import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import type { AuthSession } from '@core/store/Auth/authTypes';
import { login, logout } from '@core/store/Auth/authSlice';
import { apiSlices } from '@core/store/apis';
import { useAppDispatch, useAppSelector } from '@core/store/hooks';
import type { User } from '@/types/user';

import { clearStoredAuth, writeStoredAuth } from './tokenStorage';

interface Authentication {
  isAuthenticated: boolean;
  user: User | null;
  handleLogin: (session: AuthSession) => void;
  handleLogout: () => void;
}

// No hydration step: `authSlice` seeds the session from storage at module scope.
export function useAuthentication(): Authentication {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const user = useAppSelector((s) => s.auth.user);

  const handleLogin = useCallback(
    (session: AuthSession) => {
      writeStoredAuth({ token: session.token, user: session.user });
      dispatch(login({ token: session.token, user: session.user }));
    },
    [dispatch]
  );

  const handleLogout = useCallback(() => {
    clearStoredAuth();
    dispatch(logout());
    // Every cache is reset, or the next user sees the previous user's dashboard
    // for as long as the stale entries stay unexpired.
    apiSlices.forEach((api) => dispatch(api.util.resetApiState()));
    navigate('/login', { replace: true });
  }, [dispatch, navigate]);

  return { isAuthenticated, user, handleLogin, handleLogout };
}
